import { Helmet } from "react-helmet-async";
import SchemaMarkup from "./SchemaMarkup";

interface EnhancedSEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'homepage' | 'about' | 'pricing' | 'seo-services' | 'resources' | 'service';
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tag?: string[];
  };
}

const EnhancedSEO = ({
  title = "VGP | Premium Expert Marketing - Scale Your Business With Proven SEO & Lead Generation",
  description = "We help premium businesses attract high-value clients through targeted marketing strategies. Transform your client base with our proven SEO and lead generation systems that deliver 150% average revenue increases.",
  keywords = "premium marketing, SEO services, lead generation, digital growth, business scaling, high-ticket clients, revenue growth, digital marketing agency",
  image = "https://vincentgrowthpartners.com/opengraph-vgp.png",
  url = "https://vincentgrowthpartners.com",
  type = 'homepage',
  article
}: EnhancedSEOProps) => {
  const baseUrl = "https://vincentgrowthpartners.com";
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  
  // Enhanced title with proper SEO structure
  const seoTitle = title.length > 60 ? 
    title.substring(0, 57) + "..." : 
    title;
  
  // Enhanced description with target keywords
  const seoDescription = description.length > 160 ? 
    description.substring(0, 157) + "..." : 
    description;

  return (
    <>
      <Helmet prioritizeSeoTags>
        {/* Basic Meta Tags */}
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={keywords} />
        <meta name="author" content="Vincent Growth Partners" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={fullUrl} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content={article ? "article" : "website"} />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:image" content={image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Vincent Growth Partners - Premium Digital Marketing Services" />
        <meta property="og:site_name" content="Vincent Growth Partners" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Article specific meta tags */}
        {article && (
          <>
            {article.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
            {article.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
            {article.author && <meta property="article:author" content={article.author} />}
            {article.section && <meta property="article:section" content={article.section} />}
            {article.tag && article.tag.map(tag => (
              <meta key={tag} property="article:tag" content={tag} />
            ))}
          </>
        )}
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@vgpmarketing" />
        <meta name="twitter:creator" content="@vgpmarketing" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={image} />
        <meta name="twitter:image:alt" content="Vincent Growth Partners - Premium Digital Marketing Services" />
        
        {/* Enhanced Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, minimum-scale=1.0" />
        <meta name="theme-color" content="#192335" />
        <meta name="msapplication-TileColor" content="#192335" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="VGP Marketing" />
        
        {/* Enhanced Security Headers - Remove X-Frame-Options as it should be HTTP header */}
        <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
        <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
        <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
        
        {/* Performance Optimization - Remove ineffective Cache-Control meta tag */}
        
        {/* Critical Resource Preconnects */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* DNS Prefetch for Performance */}
        <link rel="dns-prefetch" href="//fonts.googleapis.com" />
        <link rel="dns-prefetch" href="//fonts.gstatic.com" />
        <link rel="dns-prefetch" href="//www.google-analytics.com" />
        <link rel="dns-prefetch" href="//www.googletagmanager.com" />
        
        {/* Resource Hints for Better Loading */}
        <link rel="preconnect" href="https://api.vincentgrowthpartners.com" />
        <link rel="dns-prefetch" href="//api.vincentgrowthpartners.com" />
        
        {/* Favicon and Icons */}
        <link rel="icon" type="image/png" sizes="32x32" href="/lovable-uploads/49330731-b0ec-45a5-905d-cb364a33c729.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/lovable-uploads/49330731-b0ec-45a5-905d-cb364a33c729.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/lovable-uploads/49330731-b0ec-45a5-905d-cb364a33c729.png" />
        <link rel="manifest" href="/site.webmanifest" />
        
        {/* Language and Geographic Targeting */}
        <meta name="language" content="English" />
        <meta name="geo.region" content="GB" />
        <meta name="geo.placename" content="United Kingdom" />
        <meta name="geo.position" content="51.5074;-0.1278" />
        <meta name="ICBM" content="51.5074, -0.1278" />
        
        {/* Business Information */}
        <meta name="rating" content="general" />
        <meta name="distribution" content="global" />
        <meta name="classification" content="business" />
        <meta name="category" content="Digital Marketing, SEO, Lead Generation" />
        
        {/* Rich Snippets Support */}
        <meta name="application-name" content="Vincent Growth Partners" />
        <meta name="msapplication-tooltip" content="Premium Digital Marketing Services" />
        <meta name="msapplication-starturl" content="/" />
        
        {/* Enhanced Accessibility */}
        <meta name="color-scheme" content="light dark" />
        
        {/* Critical CSS Inlining - Essential base styles */}
        <style dangerouslySetInnerHTML={{
          __html: `
            :root{--primary:219 36% 15%;--primary-foreground:39 51% 48%;--secondary:39 51% 48%;--secondary-foreground:219 36% 15%;--background:0 0% 100%;--foreground:219 36% 15%;--border:220 13% 91%;--radius:0.75rem}
            *,::before,::after{box-sizing:border-box;border-width:0;border-style:solid;border-color:hsl(var(--border))}
            html{line-height:1.5;-webkit-text-size-adjust:100%;-moz-tab-size:4;tab-size:4;font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";font-feature-settings:normal;font-variation-settings:normal}
            body{margin:0;line-height:inherit;background-color:hsl(var(--background));color:hsl(var(--foreground))}
            .container{width:100%;margin-left:auto;margin-right:auto;padding-left:1rem;padding-right:1rem}
            @media(min-width:640px){.container{max-width:640px}}
            @media(min-width:768px){.container{max-width:768px}}
            @media(min-width:1024px){.container{max-width:1024px}}
            @media(min-width:1280px){.container{max-width:1280px}}
          `
        }} />
        
        {/* Non-render-blocking CSS load */}
        <link 
          rel="preload" 
          href="/src/index.css" 
          as="style" 
          onLoad={(e) => {
            const link = e.currentTarget;
            link.onload = null;
            link.rel = 'stylesheet';
          }}
        />
        <noscript>{`<link rel="stylesheet" href="/src/index.css" />`}</noscript>
        
        {/* Preload Critical Resources */}
        <link rel="preload" href="/src/assets/vgp-logo.png" as="image" type="image/png" />
        
        {/* Module Preload for Critical JS */}
        <link rel="modulepreload" href="/src/main.tsx" />
        <link rel="modulepreload" href="/src/App.tsx" />
        
        {/* Alternative Languages - Add if multi-language support is needed */}
        <link rel="alternate" hrefLang="en-GB" href={fullUrl} />
        <link rel="alternate" hrefLang="en" href={fullUrl} />
        <link rel="alternate" hrefLang="x-default" href={fullUrl} />
      </Helmet>
      
      <SchemaMarkup 
        type={type}
        title={title}
        description={description}
        url={fullUrl}
      />
    </>
  );
};

export default EnhancedSEO;